import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, InlineStat, TrendPill } from '../../../components/ui/Card';
import { humanize, isTestSource } from '../../Marketing/shared';
import { formatDayMonthYear } from '../../../utils/dateFormat';
import type { GtmDemoBookingSources, GtmLeadFunnel, GtmMarketingData, GtmQueryMovementRow, GtmMarketConversionRow, GtmPaidEfficiency, GtmPaidCampaignRow } from '../../../api/gtm';
import { InfoTip } from '../../../components/ui/InfoTip';
import { fmtNumber, fmtCurrency, fmtPercent, fmtSigned, countryName, isSparseColumn, type PaidClickSessionRow } from './components/format';
import { CHART_SERIES } from '../../../components/ui/chart';
import { Table, THead, Th, Td, Tr, Nil, TableFooter, TTotalRow, isConstant } from '../../../components/ui/Table';
import { EmptyState, LoadingState } from '../../../components/ui/States';
import { Badge } from '../../../components/ui/Badge';
import { Pagination, usePagination } from '../../../components/ui/Pagination';

const PAGE_SIZE = 15;
const NO_ROWS: never[] = [];

/** True when at least `share` of rows are empty or zero for a column — too sparse to earn a column. */
function mostlyEmpty<T>(rows: readonly T[], pick: (row: T) => number | null | undefined, share = 0.9): boolean {
  if (rows.length === 0) return false;
  const empty = rows.filter((r) => {
    const v = pick(r);
    return v === null || v === undefined || v === 0;
  }).length;
  return empty / rows.length >= share;
}

/** The value most rows share, when at least `share` of them do; else null. */
function dominantValue<T>(rows: readonly T[], pick: (row: T) => number, share = 0.9): number | null {
  if (rows.length < 2) return null;
  const counts = new Map<number, number>();
  for (const r of rows) counts.set(pick(r), (counts.get(pick(r)) ?? 0) + 1);
  for (const [value, n] of counts) if (n / rows.length >= share) return value;
  return null;
}

/**
 * Performance-marketing panels. Marketing → Overview shows the demo funnel, paid
 * summary and top content; everything else sits in {@link PerformanceDetail}.
 * Channel contribution lives on GTM → Channels only, so the two can't disagree.
 */
export function PerformanceDetail({ data }: { data?: GtmMarketingData }) {
  return (
    <div className="space-y-6">
      {/* Answers first (where leads come from, what each source brought), then email, search movement,
          and G2 last: it is a signal that goes quiet for weeks. */}
      <MarketConversion rows={data?.marketConversion ?? null} funnel={data?.leadFunnel ?? null} />
      <Attribution data={data} />
      <EmailPerformance data={data} />
      <FixQueue data={data} />
      <QueryMovement data={data} />
      <BuyerIntent data={data} />
    </div>
  );
}

/**
 * Two cards side by side only when they are about the same height: a long table
 * beside a three-row (or empty) card leaves a half-empty cell, so then they stack.
 */
export function PairRow({ sizes, left, right }: { sizes: [number, number]; left: ReactNode; right: ReactNode }) {
  const compact = (n: number) => n <= 3;
  const pair = compact(sizes[0]) === compact(sizes[1]);
  return pair ? (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      {left}
      {right}
    </div>
  ) : (
    <>
      {left}
      {right}
    </>
  );
}

export function TopContent({ data }: { data?: GtmMarketingData }) {
  const lo = data?.linkedinOrganic;
  if (!lo) {
    return (
      <Card>
        <CardHeader title="Top content" description="Organic LinkedIn posts" />
        <EmptyState compact title="No LinkedIn post data for this period." />
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader title="Top content" description="Organic LinkedIn posts, latest snapshot per post" />
      <div className="mb-6 grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4">
        <InlineStat label="Posts" value={fmtNumber(lo.totals.posts)} />
        <InlineStat label="Impressions" value={fmtNumber(lo.totals.impressions)} />
        <InlineStat label="Engagements" value={fmtNumber(lo.totals.engagements)} />
        <InlineStat label="Avg eng. rate" value={lo.totals.avgEngagementRate === null ? '—' : fmtPercent(lo.totals.avgEngagementRate)} />
      </div>
      {lo.topPosts.length > 0 && (
        <Table>
          <THead>
            <tr>
              <Th>Post</Th>
              <Th align="right">Impr.</Th>
              <Th align="right">Eng.</Th>
              <Th align="right">Rate</Th>
            </tr>
          </THead>
          <tbody>
            {lo.topPosts.map((p) => (
              <Tr key={p.contentItemId}>
                <Td className="text-text-primary break-words max-w-xs" title={p.title}>{p.title}</Td>
                <Td align="right">{fmtNumber(p.impressions)}</Td>
                <Td align="right">{fmtNumber(p.engagements)}</Td>
                <Td align="right">{p.engagementRate === null ? <Nil /> : fmtPercent(p.engagementRate)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function EmailPerformance({ data }: { data?: GtmMarketingData }) {
  const ep = data?.emailPerformance;
  // QA sends ("Test newsletter", sends to the test-sequesto.com domain) aren't marketing data.
  const tests = ep?.campaigns.filter((c) => isTestSource(c.name)) ?? [];
  const campaigns = useMemo(() => ep?.campaigns.filter((c) => !isTestSource(c.name)) ?? NO_ROWS, [ep]);
  const pager = usePagination(campaigns, PAGE_SIZE);
  const totals = ep ? withoutTests(ep.totals, tests) : null;
  const oneKind = isConstant(campaigns, (c) => c.kind);
  // Say which kinds were sent: "campaign & newsletter" over three newsletters promised rows that weren't there.
  const hasCampaignKind = campaigns.some((c) => c.kind === 'campaign');
  const hasNewsletter = campaigns.some((c) => c.kind === 'newsletter');
  const kindNote =
    hasNewsletter && !hasCampaignKind
      ? ' · newsletters only, no automated campaign sent in the period'
      : hasCampaignKind && !hasNewsletter
        ? ' · campaigns only, no newsletter sent in the period'
        : '';
  // The 45-day limit only matters when the period reaches further back than Customer.io keeps.
  const coverageCut = !!ep?.campaignsCoveredFrom && !!data?.period?.startDate && data.period.startDate < ep.campaignsCoveredFrom;
  return (
    <Card>
      <CardHeader
        title="Email performance"
        description={`Customer.io sends, opens, clicks and conversions${kindNote}${
          coverageCut && ep?.campaignsCoveredFrom
            ? ` · campaign rows start ${formatDayMonthYear(ep.campaignsCoveredFrom)}: Customer.io keeps 45 days`
            : ''
        }`}
        action={
          <InfoTip label="How opens and clicks are counted">
            Rates are of delivered emails, not of sent. Opens and clicks are the ones Customer.io counts as human:
            Apple Mail’s automatic prefetch opens and the security scanners that click every link in a mail are left
            out. Its filter doesn’t catch every scanner, so read the click rate as an upper bound. Marketing →
            Website, “Where sessions come from”, shows the visits email actually sent.
          </InfoTip>
        }
      />
      {!ep || !totals || campaigns.length === 0 ? (
        <EmptyState compact title="No email sends in this period." />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-5">
            <InlineStat label="Sent" value={fmtNumber(totals.sent)} />
            <Deliverability sent={totals.sent} delivered={totals.delivered} />
            <InlineStat
              label="Open rate"
              value={totals.openRate === null ? '—' : fmtPercent(totals.openRate * 100)}
              sub={`${fmtNumber(totals.humanOpened)} human opens, of delivered`}
            />
            <InlineStat
              label="Click rate (upper bound)"
              value={totals.clickRate === null ? '—' : fmtPercent(totals.clickRate * 100)}
              sub={`${fmtNumber(totals.humanClicked)} human clicks, of delivered`}
            />
            <InlineStat label="Conversions" value={fmtNumber(totals.converted)} sub="Customer.io’s conversion goal" />
          </div>
          <Table minWidth={720}>
            <THead>
              <tr>
                <Th>Campaign</Th>
                {!oneKind && <Th>Type</Th>}
                <Th align="right">Sent</Th>
                <Th align="right">Delivered</Th>
                <Th align="right">Human opens</Th>
                <Th align="right">Open rate</Th>
                <Th align="right">Human clicks</Th>
                <Th align="right">Conv.</Th>
                <Th align="right">Unsub.</Th>
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((c) => (
                <Tr key={`${c.kind}-${c.id}`}>
                  <Td className="text-text-primary break-words max-w-xs" title={c.name}>{c.name}</Td>
                  {!oneKind && <Td className="text-text-tertiary">{humanize(c.kind)}</Td>}
                  <Td align="right">{fmtNumber(c.sent)}</Td>
                  <Td align="right">
                    {c.sent > 0 && c.delivered / c.sent < DELIVERABILITY_FLOOR ? (
                      <span title={`${fmtPercent((c.delivered / c.sent) * 100, 0)} of sent delivered`}>
                        <Badge variant="warning">{fmtNumber(c.delivered)}</Badge>
                      </span>
                    ) : (
                      fmtNumber(c.delivered)
                    )}
                  </Td>
                  <Td align="right">{fmtNumber(c.humanOpened)}</Td>
                  <Td align="right">{c.openRate === null ? <Nil /> : fmtPercent(c.openRate * 100)}</Td>
                  <Td align="right">{fmtNumber(c.humanClicked)}</Td>
                  <Td align="right">{fmtNumber(c.converted)}</Td>
                  <Td align="right">{fmtNumber(c.unsubscribed)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <Pagination {...pager} onPage={pager.setPage} />
        </>
      )}
      {ep && totals && campaigns.length > 0 && (
        <TableFooter>
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span>
              {[
                totals.sent > 0 &&
                  totals.delivered < totals.sent * DELIVERABILITY_FLOOR &&
                  `${fmtNumber(totals.sent - totals.delivered)} of ${fmtNumber(totals.sent)} sent (${fmtPercent(((totals.sent - totals.delivered) / totals.sent) * 100, 0)}) weren’t delivered`,
                tests.length > 0 && `${tests.length} test ${tests.length === 1 ? 'row' : 'rows'} hidden, and left out of the totals`,
                coverageCut &&
                  ep.campaignsCoveredFrom &&
                  `Campaign figures cover only from ${formatDayMonthYear(ep.campaignsCoveredFrom)}`,
              ]
                .filter(Boolean)
                .join(' · ') || 'Customer.io, per campaign'}
            </span>
            <InfoTip label="Email notes">
              {totals.sent > 0 &&
                totals.delivered < totals.sent * DELIVERABILITY_FLOOR &&
                `Customer.io counts bounces, suppressed and dropped sends as not delivered, and the split isn’t synced, so check the campaign in Customer.io. Amber delivered counts are under ${DELIVERABILITY_FLOOR * 100}% of sent. `}
              Conversions are Customer.io’s own goal (a recipient who converted within its attribution window after a send); the “customerio / email” row under Lead-form submissions by UTM source counts form submissions that carried that UTM, a different count.
              {coverageCut && ' Customer.io keeps 45 days of daily campaign data (newsletters are complete).'}
            </InfoTip>
          </span>
        </TableFooter>
      )}
    </Card>
  );
}

/** Below this delivered share, sending is worth a look (bounces, blocks, a bad list). */
const DELIVERABILITY_FLOOR = 0.9;

/** Delivered count with its share of sent; amber when under {@link DELIVERABILITY_FLOOR}. */
function Deliverability({ sent, delivered }: { sent: number; delivered: number }) {
  const share = sent > 0 ? delivered / sent : null;
  const low = share !== null && share < DELIVERABILITY_FLOOR;
  return (
    <div className="min-w-0">
      <InlineStat label="Delivered" value={fmtNumber(delivered)} sub={low ? undefined : share === null ? undefined : `${fmtPercent(share * 100, 0)} of sent`} />
      {low && (
        <span className="mt-1 inline-flex" title={`Under ${DELIVERABILITY_FLOOR * 100}% of sent emails were delivered`}>
          <Badge variant="warning">{fmtPercent(share * 100, 0)} of sent delivered</Badge>
        </span>
      )}
    </div>
  );
}

/** API totals minus the hidden test campaigns, with the rates worked out again. */
function withoutTests(
  totals: NonNullable<GtmMarketingData['emailPerformance']>['totals'],
  tests: NonNullable<GtmMarketingData['emailPerformance']>['campaigns'],
) {
  if (tests.length === 0) return totals;
  const sum = (pick: (c: (typeof tests)[number]) => number) => tests.reduce((n, c) => n + pick(c), 0);
  const delivered = totals.delivered - sum((c) => c.delivered);
  const humanOpened = totals.humanOpened - sum((c) => c.humanOpened);
  const humanClicked = totals.humanClicked - sum((c) => c.humanClicked);
  return {
    sent: totals.sent - sum((c) => c.sent),
    delivered,
    opened: totals.opened - sum((c) => c.opened),
    clicked: totals.clicked - sum((c) => c.clicked),
    humanOpened,
    humanClicked,
    converted: totals.converted - sum((c) => c.converted),
    unsubscribed: totals.unsubscribed - sum((c) => c.unsubscribed),
    openRate: delivered > 0 ? humanOpened / delivered : null,
    clickRate: delivered > 0 ? humanClicked / delivered : null,
  };
}

/** G2 cuts some company names mid-bracket ("Bergquist Consulting (Sole Proprietorship"): close what it left open. */
function closeParens(name: string): string {
  const open = (name.match(/\(/g) ?? []).length - (name.match(/\)/g) ?? []).length;
  return open > 0 ? `${name}${')'.repeat(open)}` : name;
}

/** Past this many days without a G2 signal the card collapses to one line. */
const G2_STALE_DAYS = 14;

function BuyerIntent({ data }: { data?: GtmMarketingData }) {
  const rows = data?.buyerIntent;
  const pager = usePagination(rows ?? NO_ROWS, PAGE_SIZE);
  // Nearly every company researches one category: say it once, and only call out the exceptions.
  const usualCategories = dominantValue(rows ?? NO_ROWS, (r) => r.categoryCount);
  // Freshness up front: on 12 Oct 24 of 25 rows were from July and August, the newest 6 Oct.
  const latest = (rows ?? NO_ROWS).reduce<string | null>((m, r) => (r.lastSeen && (!m || r.lastSeen > m) ? r.lastSeen : m), null);
  // A signal that went quiet is one line, not a table of last month's visitors.
  const daysSince = latest ? Math.floor((Date.now() - new Date(latest).getTime()) / 86_400_000) : null;
  if (rows && rows.length > 0 && daysSince !== null && daysSince > G2_STALE_DAYS) {
    return (
      <p className="px-1 text-body-xs text-text-tertiary">
        G2 buyer intent: no new signal since {formatDayMonthYear(latest!)} ({daysSince} days ago); {fmtNumber(rows.length)}{' '}
        {rows.length === 1 ? 'company' : 'companies'} researched our categories earlier in the period.
      </p>
    );
  }
  return (
    <Card>
      <CardHeader
        title="G2 buyer intent"
        description={`Companies researching our categories on G2, an inbound demand signal${latest ? ` · latest signal ${formatDayMonthYear(latest)}${daysSince !== null && daysSince > 3 ? ` (${daysSince} days ago)` : ''}` : ''}`}
      />
      {!rows || rows.length === 0 ? (
        <EmptyState compact title="No G2 buyer-intent signals for this period." />
      ) : (
        <>
          <Table colWidths={usualCategories === null ? ['auto', '4.5rem', '6rem', '7.5rem'] : ['auto', '4.5rem', '7.5rem']}>
            <THead>
              <tr>
                <Th>Company</Th>
                <Th align="right">Signals</Th>
                {usualCategories === null && <Th align="right">Categories</Th>}
                <Th align="right" className="!pr-3">Last seen</Th>
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((r) => (
                <Tr key={(r.companyDomain ?? r.companyName) ?? r.lastSeen}>
                  <Td className="text-text-primary break-words" title={r.companyName ?? r.companyDomain ?? ''}>
                    {r.companyName ? closeParens(r.companyName) : (r.companyDomain ?? <Nil />)}
                    {r.categories && r.categories.length > 0 ? (
                      <span className="block text-body-xs text-text-tertiary">{r.categories.join(' · ')}</span>
                    ) : (
                      usualCategories !== null &&
                      r.categoryCount !== usualCategories && (
                        <span className="text-body-xs text-text-tertiary"> · {fmtNumber(r.categoryCount)} categories</span>
                      )
                    )}
                  </Td>
                  <Td align="right">{fmtNumber(r.signalCount)}</Td>
                  {usualCategories === null && <Td align="right">{fmtNumber(r.categoryCount)}</Td>}
                  <Td align="right" className="whitespace-nowrap text-text-tertiary !pr-3">{formatDayMonthYear(r.lastSeen)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <Pagination {...pager} onPage={pager.setPage} />
          {usualCategories !== null && (
            <TableFooter>
              Companies research {usualCategories} G2 {usualCategories === 1 ? 'category' : 'categories'} unless noted.
            </TableFooter>
          )}
        </>
      )}
    </Card>
  );
}

/** One rate rule on the page: one decimal, two only under 1% (a real fraction of a percent). */
function fmtRate(pct: number): string {
  return fmtPercent(pct, pct > 0 && pct < 1 ? 2 : 1);
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** "1–9 Sep": the same days of last month that month-to-date is compared with (UTC, like the API). */
function sameDaysLastMonthLabel(now = new Date()): string {
  const lastMonthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const lastMonthDays = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0)).getUTCDate();
  const day = Math.min(now.getUTCDate(), lastMonthDays);
  // Fixed names: en-GB's month: 'short' prints "Sept" (seen on 13 Oct).
  const month = MONTHS_SHORT[lastMonthStart.getUTCMonth()];
  return day === 1 ? `1 ${month}` : `1–${day} ${month}`;
}

/** "1–14 Oct": this month so far, the window the demo tiles count. */
function monthToDateLabel(now = new Date()): string {
  const month = MONTHS_SHORT[now.getUTCMonth()];
  return now.getUTCDate() === 1 ? `1 ${month}` : `1–${now.getUTCDate()} ${month}`;
}

/**
 * A demo tile: StatTile's layout, with a trend pill whose colour follows whether
 * the change is good — fewer cancellations is a green ↓, not a red one.
 * (StatTile's `change` can't express that yet; see the kit request in the PR.)
 */
function DemoTile({
  label,
  value,
  change,
  sub,
  tooltip,
}: {
  label: string;
  value: string;
  change?: { text: string; trend: 'up' | 'down' | 'neutral'; tone: 'good' | 'bad' | 'neutral' };
  sub: string;
  tooltip?: string;
}) {
  return (
    <Card padding="md">
      <div className="[container-type:inline-size]">
        <p className={`eyebrow truncate ${tooltip ? 'cursor-help' : ''}`} title={tooltip}>
          {label}
        </p>
        <p className="mt-3 text-[clamp(1.5rem,12cqi,2.5rem)] font-light leading-none tracking-[-0.03em] tabular-nums text-text-primary">
          {value}
        </p>
        {/* No change is said by the sub line ("9 in 1–13 Sep"); a grey "→ 0" pill adds nothing. */}
        {change && change.trend !== 'neutral' && (
          <div className="mt-3">
            <TrendPill trend={change.trend} tone={change.tone}>
              {change.text}
            </TrendPill>
          </div>
        )}
        <p className="mt-1 text-body-xs text-text-tertiary">{sub}</p>
      </div>
    </Card>
  );
}

export function DemoFunnel({ data }: { data: GtmMarketingData }) {
  const d = data.demoFunnel!;
  // Month-to-date against the same days of last month (the API counts both the
  // same way), so a 9-day month is never set against a full one. The arrow
  // shows the direction; the colour shows whether that direction is good.
  const mtd = (cur: number, prev: number, higherIsBetter: boolean) => {
    const diff = cur - prev;
    const trend: 'up' | 'down' | 'neutral' = diff > 0 ? 'up' : diff < 0 ? 'down' : 'neutral';
    const tone: 'good' | 'bad' | 'neutral' = diff === 0 ? 'neutral' : diff > 0 === higherIsBetter ? 'good' : 'bad';
    return { text: fmtSigned(diff), trend, tone };
  };
  const tm = d.thisMonth;
  const lm = d.lastMonth;
  const vs = sameDaysLastMonthLabel();
  const sub = (prev: number | null) => (prev === null ? `vs ${vs}: unavailable` : `${fmtNumber(prev)} in ${vs}`);
  return (
    <div>
      {/* Always the calendar month, whatever range is picked above: the header says so plainly, the why sits in the (i). */}
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-body-md font-semibold text-text-primary">Demos this month</h3>
        <div className="flex items-center gap-1.5">
          <p className="text-body-xs text-text-tertiary">
            {monthToDateLabel()} vs {vs}
          </p>
          <InfoTip label="About demos this month">
            <div className="space-y-1.5">
              <p>
                Always this calendar month to date, whatever range is selected above, against the same days last month
                so a part month is never set against a full one.
              </p>
              <p>From Calendly: the same demo count Sales shows. Demo page views are from GA4.</p>
              {tm.noShows === null && <p>No-shows aren’t shown: nobody marks them in Calendly yet.</p>}
            </div>
          </InfoTip>
        </div>
      </div>
      <div className={`grid grid-cols-2 md:grid-cols-3 ${tm.noShows === null ? 'lg:grid-cols-4' : 'lg:grid-cols-5'} gap-4`}>
        <DemoTile
          label="Demos done"
          value={fmtNumber(tm.occurred)}
          change={mtd(tm.occurred, lm.occurred, true)}
          sub={sub(lm.occurred)}
          tooltip="Demos this month whose meeting time has passed and that were not cancelled. Same demos as Sales and the “Demos booked” tile: demo meetings only, tests out, a person rebooking within 30 days once."
        />
        {/* Shown only once someone records no-shows in Calendly; until then a note below says so. */}
        {tm.noShows !== null && (
          <DemoTile
            label="No-shows"
            value={fmtNumber(tm.noShows)}
            change={lm.noShows !== null ? mtd(tm.noShows, lm.noShows, false) : undefined}
            sub={sub(lm.noShows)}
            tooltip="Demos done this month that the host marked no-show in Calendly."
          />
        )}
        <DemoTile
          label="Canceled"
          value={fmtNumber(tm.canceled)}
          change={mtd(tm.canceled, lm.canceled, false)}
          sub={sub(lm.canceled)}
          tooltip="Demos this month where every booking was cancelled (a cancel-and-rebook is one demo that is on)."
        />
        <DemoTile
          label="Upcoming"
          value={fmtNumber(d.upcoming)}
          sub="Scheduled from today"
          tooltip="Demos still on with the meeting ahead, any month (Sales’ demo count)."
        />
        <DemoTile
          label="Demo page views"
          value={tm.demoPageViews === null ? '—' : fmtNumber(tm.demoPageViews)}
          change={tm.demoPageViews !== null && lm.demoPageViews !== null ? mtd(tm.demoPageViews, lm.demoPageViews, true) : undefined}
          sub={tm.demoPageViews === null ? 'GA4 unavailable' : sub(lm.demoPageViews)}
        />
      </div>
    </div>
  );
}

const PLATFORM_LABELS: Record<string, string> = {
  linkedin: 'LinkedIn',
  google: 'Google',
  meta: 'Meta',
  bing: 'Microsoft Ads',
};

/** A campaign row whose spend is Odoo's monthly bills (Bing), not an ad platform's daily report. */
const fromBills = (c: GtmPaidCampaignRow) => c.spendSource === 'odoo_bills';

const PAID_PLATFORM_LABELS: Record<string, string> = { ...PLATFORM_LABELS, bing: 'Bing', other: 'other paid' };

/** "48 LinkedIn (47 lead forms + 1 UTM)": a LinkedIn count mixes lead gen forms and paid website visits. */
function platformLeads(p: GtmLeadFunnel['paidByPlatform'][number]): string {
  const label = `${fmtNumber(p.leads)} ${PAID_PLATFORM_LABELS[p.platform] ?? p.platform}`;
  const forms = p.viaLeadForm ?? 0;
  if (forms === 0) return label;
  const rest = p.leads - forms;
  return rest > 0 ? `${label} (${fmtNumber(forms)} lead forms + ${fmtNumber(rest)} UTM)` : `${label} (all lead forms)`;
}

/**
 * "Google 1,138 clicks → 233 tracked sessions": what each platform bills as a
 * click against what OS tracking saw arrive. Tracking counts cookie-consented
 * visitors only, so the gap is never zero; a large one is worth a look.
 */
function PaidClicksToSessions({ state, linkedinLeads }: { state: PaidSessionsState; linkedinLeads: LinkedInLeads }) {
  if (state === 'loading') return <p className="mb-4 text-body-xs text-text-tertiary">Loading tracked sessions per platform…</p>;
  if (state === 'error') {
    return <p className="mb-4 text-body-xs text-text-tertiary">Clicks → tracked sessions unavailable: website tracking didn’t load.</p>;
  }
  if (state.length === 0) return null;
  const note = linkedinNote(state, linkedinLeads);
  return (
    <div className="mb-6">
      <p className="text-body-xs font-medium text-text-secondary">
        Clicks → tracked sessions
        <span className="font-normal text-text-tertiary"> · ad platforms vs OS tracking, same period · tracking counts cookie-consented visitors only</span>
      </p>
      <ul className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-body-sm text-text-primary tabular-nums">
        {state.map((r) => (
          <li key={r.label}>
            {r.clicks === null ? (
              <>
                {r.label}: {fmtNumber(r.sessions)} {r.sessions === 1 ? 'session' : 'sessions'}
                <span className="text-text-tertiary">
                  {r.spendFromBills ? ', spend from Odoo bills (no click counts until Microsoft Ads syncs)' : ', no spend source connected'}
                </span>
              </>
            ) : (
              <>
                {r.label} {fmtNumber(r.clicks)} → {fmtNumber(r.sessions)}
              </>
            )}
          </li>
        ))}
      </ul>
      {note && <p className="mt-1 text-body-xs text-text-tertiary">{note}</p>}
    </div>
  );
}

type LinkedInLeads = GtmLeadFunnel['paidByPlatform'][number] | undefined;

/**
 * Why LinkedIn read "420 → 0" on 12 Oct (90 days): its campaigns run lead gen
 * forms, which open inside LinkedIn, and LinkedIn counts those opens as clicks.
 * All 45 LinkedIn leads came through forms and no session carried a LinkedIn
 * paid UTM. Untagged clicks with li_fat_id now count as paid LinkedIn
 * (web-channel.ts), so the note goes once any arrive.
 */
function linkedinNote(state: PaidClickSessionRow[], leads: LinkedInLeads): string | null {
  const li = state.find((r) => r.label === 'LinkedIn');
  if (!li || li.clicks === null || li.clicks === 0 || li.sessions > 0) return null;
  const viaForm = leads?.viaLeadForm ?? 0;
  const forms =
    viaForm === 0 ? '' : viaForm === leads?.leads ? ` (all ${fmtNumber(viaForm)} LinkedIn leads came that way)` : ` (${fmtNumber(viaForm)} of ${fmtNumber(leads!.leads)} LinkedIn leads came that way)`;
  return `LinkedIn counts lead gen form opens as clicks; those stay on LinkedIn${forms}, so they never reach the site. Website-click ads show here once their URLs carry utm_medium=paid_social or li_fat_id.`;
}

export type PaidSessionsState = PaidClickSessionRow[] | 'loading' | 'error';

export function PaidSummary({
  data,
  funnel,
  periodLabel,
  paidSessions,
}: {
  data: GtmPaidEfficiency | null;
  funnel: GtmLeadFunnel | null;
  periodLabel: string;
  /** Per-platform clicks vs OS-tracked sessions (see paidClickSessions). */
  paidSessions: PaidSessionsState;
}) {
  const campaigns = useMemo(() => (data ? [...data.campaigns].sort((x, y) => y.spend - x.spend) : NO_ROWS), [data]);
  const pager = usePagination(campaigns, PAGE_SIZE);
  const leadsSub = funnel
    ? funnel.paidByPlatform.map(platformLeads).join(' · ') || 'none'
    : 'lead count unavailable';
  // Platform-reported conversions sit on 2 campaigns in most periods: a column of
  // dots. Show it only when a real share of rows has one; else list them in the footer.
  const convRows = campaigns.filter((c) => (c.leads ?? 0) > 0);
  const showPlatformConv = convRows.length > 0 && !isSparseColumn(campaigns, (c) => (c.leads ?? 0) > 0);
  const hasFormLeads = campaigns.some((c) => c.formLeads !== undefined);
  const unmatched = data?.totals.unmatchedFormLeads ?? 0;
  const sum = (pick: (c: (typeof campaigns)[number]) => number) => campaigns.reduce((n, c) => n + pick(c), 0);
  const matchedLeads = sum((c) => c.formLeads ?? 0);
  // No won deal from paid yet: €0 and 0.0× read as results, so they wait for the first one.
  const hasWon = (data?.totals.wonValue ?? 0) > 0;
  // The exception to act on: a lead-seeking campaign that spent over twice the average cost per
  // lead with no lead (90 days, 13 Oct: UK-EN | Search | Generic, €3,587). Awareness campaigns
  // (_AW_) aren't meant to bring leads; billed rows can't be matched.
  const cpl = data?.totals.costPerLead ?? null;
  const isAwareness = (c: (typeof campaigns)[number]) => /awareness|_AW_/i.test(c.name);
  const overSpendNoLead = (c: (typeof campaigns)[number]) =>
    hasFormLeads && cpl !== null && !fromBills(c) && (c.formLeads ?? 0) === 0 && c.spend > 2 * cpl;
  const noLeadSpend = (c: (typeof campaigns)[number]) => overSpendNoLead(c) && !isAwareness(c);
  const flagged = campaigns.filter(noLeadSpend);
  // Same test, but not flagged: awareness campaigns aren't meant to bring leads. Said in the footer so the count adds up.
  const awarenessSkipped = campaigns.filter((c) => overSpendNoLead(c) && isAwareness(c));
  const bingUnavailable = data?.totals.bingSpendUnavailable;
  const billedBing = campaigns.some(fromBills);
  return (
    <Card>
      <CardHeader
        title="Paid spend"
        description={`${periodLabel} · LinkedIn, Google and Meta Ads${billedBing ? ', Microsoft Ads from Odoo bills (monthly)' : ''}; leads are form leads from paid`}
        action={
          <Link to="/gtm/channels" className="text-body-xs text-accent hover:underline">
            Channel breakdown: see GTM → Channels
          </Link>
        }
      />
      {!data || data.campaigns.length === 0 ? (
        <EmptyState compact title="No active campaigns with spend in this period." />
      ) : (
        <>
          {/* Inline stats, not tiles: a card inside a card doubles every border and squeezed CPL. */}
          <div className="mb-6 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-5">
            <InlineStat label="Spend" value={fmtCurrency(data.totals.spend)} />
            <InlineStat
              label="Leads from paid"
              value={data.totals.formLeads === null ? '—' : fmtNumber(data.totals.formLeads)}
              sub={leadsSub}
            />
            <InlineStat
              label="Cost per lead"
              value={data.totals.costPerLead === null ? '—' : fmtCurrency(data.totals.costPerLead)}
              sub="spend ÷ leads from paid"
            />
            {hasWon ? (
              <>
                <InlineStat label="Won (paid-sourced)" value={fmtCurrency(data.totals.wonValue!)} />
                <InlineStat label="ROAS" value={data.totals.roas === null ? '—' : `${data.totals.roas.toFixed(1)}×`} />
              </>
            ) : (
              <InlineStat
                label="Won (paid-sourced)"
                value="—"
                sub={data.totals.wonValue === null ? 'Attio unavailable' : 'No won deal from paid yet; ROAS shows after the first'}
              />
            )}
          </div>
          <PaidClicksToSessions
            state={paidSessions}
            linkedinLeads={funnel?.paidByPlatform.find((p) => p.platform === 'linkedin')}
          />
          <Table
            minWidth={showPlatformConv ? 760 : 680}
            // "Platform conv." needs its own room: at 5.5rem its uppercase header ran into "Leads".
            colWidths={['auto', '6.5rem', '7rem', '5rem', ...(showPlatformConv ? ['8.5rem'] : []), ...(hasFormLeads ? ['5.5rem', '6.5rem'] : [])]}
          >
            <THead>
              <tr>
                <Th>Campaign</Th>
                <Th align="right">Spend</Th>
                <Th align="right">Impressions</Th>
                <Th align="right">Clicks</Th>
                {showPlatformConv && (
                  <Th align="right" className="whitespace-normal leading-tight" title="Conversions the ad platform reports. Not the lead count.">Platform conv.</Th>
                )}
                {hasFormLeads && (
                  <>
                    <Th align="right" title="Form leads from paid matched to this campaign">Leads</Th>
                    <Th align="right" title="Spend ÷ leads from paid">Cost/lead</Th>
                  </>
                )}
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((c) => (
                <Tr key={`${c.platform}-${c.campaignId}`}>
                  <Td>
                    <div className="break-words text-text-primary" title={c.name}>{c.name}</div>
                    <div className="text-body-xs text-text-tertiary">
                      {fromBills(c) ? c.spendNote : PLATFORM_LABELS[c.platform] ?? humanize(c.platform)}
                    </div>
                  </Td>
                  <Td
                    align="right"
                    className={noLeadSpend(c) ? 'text-warning-dark' : undefined}
                    title={noLeadSpend(c) ? `No lead from paid matched to this campaign for ${fmtCurrency(c.spend)}, over twice the average cost per lead` : undefined}
                  >
                    {fmtCurrency(c.spend)}
                  </Td>
                  <Td align="right">{fromBills(c) ? <Nil /> : fmtNumber(c.impressions)}</Td>
                  <Td align="right">{fromBills(c) ? <Nil /> : fmtNumber(c.clicks)}</Td>
                  {showPlatformConv && <Td align="right">{c.leads ? fmtNumber(c.leads) : <Nil />}</Td>}
                  {hasFormLeads && (
                    <>
                      <Td align="right">{c.formLeads ? fmtNumber(c.formLeads) : <Nil />}</Td>
                      <Td align="right">{c.formLeads ? fmtCurrency(c.spend / c.formLeads) : <Nil />}</Td>
                    </>
                  )}
                </Tr>
              ))}
              {hasFormLeads && unmatched > 0 && (
                <Tr>
                  <Td className="text-text-tertiary">Paid leads not matched to a campaign</Td>
                  <Td align="right"><Nil /></Td>
                  <Td align="right"><Nil /></Td>
                  <Td align="right"><Nil /></Td>
                  {showPlatformConv && <Td align="right"><Nil /></Td>}
                  <Td align="right">{fmtNumber(unmatched)}</Td>
                  <Td align="right"><Nil /></Td>
                </Tr>
              )}
              <TTotalRow>
                <Td>All campaigns</Td>
                <Td align="right">{fmtCurrency(data.totals.spend)}</Td>
                <Td align="right">{fmtNumber(data.totals.impressions)}</Td>
                <Td align="right">{fmtNumber(data.totals.clicks)}</Td>
                {showPlatformConv && <Td align="right">{fmtNumber(data.totals.leads)}</Td>}
                {hasFormLeads && (
                  <>
                    <Td align="right">{fmtNumber(matchedLeads + unmatched)}</Td>
                    <Td align="right">{data.totals.costPerLead === null ? <Nil /> : fmtCurrency(data.totals.costPerLead)}</Td>
                  </>
                )}
              </TTotalRow>
            </tbody>
          </Table>
          <Pagination {...pager} onPage={pager.setPage} />
          <TableFooter>
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <span>
                {flagged.length > 0
                  ? `${flagged.length === 1 ? '1 campaign' : `${flagged.length} campaigns`} in amber spent over twice the average cost per lead (${fmtCurrency(cpl!)}) with no lead: ${flagged.map((c) => `${c.name} ${fmtCurrency(c.spend)}`).join(', ')}.`
                  : 'No lead-seeking campaign spent over twice the average cost per lead with no lead.'}
                {awarenessSkipped.length > 0 &&
                  ` Not flagged, awareness: ${awarenessSkipped.map((c) => `${c.name} ${fmtCurrency(c.spend)}`).join(', ')}.`}
                {bingUnavailable && ` Bing spend from Odoo bills didn’t load (${bingUnavailable}), so spend and cost per lead leave it out.`}
              </span>
              <InfoTip label="Paid spend notes">
                Leads from paid = people who sent a LinkedIn lead gen form (always paid) or a website form reached through
                paid UTMs or an ad click id. A lead is matched to a campaign by the LinkedIn form’s campaign, or the form’s
                utm_campaign / Google campaign id; the rest sit in their own row, so the column adds up to the tile.
                {showPlatformConv
                  ? ` “Platform conv.” is what each platform reports (${fmtNumber(data.totals.leads)} in all), a different count.`
                  : convRows.length > 0
                    ? ` The platforms’ own conversion counts, a different number, are on ${convRows.length} of ${campaigns.length} campaigns only, so that column is hidden: ${convRows
                        .map((c) => `${c.name} ${fmtNumber(c.leads ?? 0)}`)
                        .join(', ')} (${fmtNumber(data.totals.leads)} in all).`
                    : ' The platforms report no conversions of their own here, so that column is hidden.'}
              </InfoTip>
            </span>
          </TableFooter>
        </>
      )}
    </Card>
  );
}

function Attribution({ data }: { data?: GtmMarketingData }) {
  const a = data?.attribution;
  return (
    <Card>
      <CardHeader
        title="Lead-form submissions by UTM source"
        description="The funnel’s lead-form submissions in the period (website forms and LinkedIn lead gen forms), by the UTM each carried"
      />
      {!a ? (
        <EmptyState compact title="No form submissions in this period." />
      ) : (
        <div>
          <Table>
            <THead>
              <tr>
                <Th>Source</Th>
                <Th>Medium</Th>
                <Th align="right">Submissions</Th>
              </tr>
            </THead>
            <tbody>
              {a.utmSources.map((r) => (
                <Tr key={`${r.source}-${r.firstTouchFallback ?? ''}-${r.medium ?? ''}`}>
                  <Td className="text-text-primary">
                    {r.firstTouchFallback ? `${r.source} → first touch: ${r.firstTouchFallback}` : r.source}
                  </Td>
                  <Td className="text-text-secondary">{r.medium ?? <Nil />}</Td>
                  <Td align="right">{fmtNumber(r.count)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <TableFooter>
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <span>
                {fmtNumber(a.totalSubmissions)} submissions
                {a.leads != null && ` from the funnel’s ${fmtNumber(a.leads)} leads (a person can send more than one form)`}.
              </span>
              {a.firstTouchResolved !== undefined && (a.firstTouchResolved > 0 || (a.unknownSource ?? 0) > 0) && (
                <span>
                  “(none) → first touch” rows had no UTM source; their source is the person’s first tracked visit ({fmtNumber(a.firstTouchResolved)}).
                  Still unknown: {fmtNumber(a.unknownSource ?? 0)}.
                </span>
              )}
              {a.excluded && (
                <InfoTip label="What is left out">
                  Left out, by the funnel’s rule: {fmtNumber(a.excluded.tests)} test (test address or a “test” UTM) and{' '}
                  {fmtNumber(a.excluded.otherFormSubmissions)} non-lead-form submissions.
                </InfoTip>
              )}
            </span>
          </TableFooter>
        </div>
      )}
    </Card>
  );
}

/** The fix queue's position cap, as the API applies it (older payloads listed pages at 30–52). */
const FIX_QUEUE_MAX_POSITION = 20;
const SITE_ORIGIN = 'https://sequesto.com';

function FixQueue({ data }: { data?: GtmMarketingData }) {
  const rows = data?.fixQueue?.filter((r) => r.position <= FIX_QUEUE_MAX_POSITION);
  const pager = usePagination(rows ?? NO_ROWS, PAGE_SIZE);
  return (
    <Card>
      <CardHeader
        title="Title / meta fix queue"
        description="High-impression, low-CTR pages already ranking in the top 20, where a better title or description earns clicks (GSC)"
      />
      {!rows || rows.length === 0 ? (
        <EmptyState compact title="No fix candidates (GSC unavailable or none qualify)." />
      ) : (
        <>
        <Table>
          <THead>
            <tr>
              <Th>Page</Th>
              <Th align="right">Impressions</Th>
              <Th align="right">Clicks</Th>
              <Th align="right">CTR</Th>
              <Th align="right">Position</Th>
            </tr>
          </THead>
          <tbody>
            {pager.pageItems.map((r) => (
              <Tr key={r.page}>
                <Td className="break-words">
                  <a href={`${SITE_ORIGIN}${stripDomain(r.page)}`} target="_blank" rel="noreferrer" className="text-accent hover:underline" title={r.page}>
                    {stripDomain(r.page)}
                  </a>
                </Td>
                <Td align="right">{fmtNumber(r.impressions)}</Td>
                <Td align="right">{fmtNumber(r.clicks)}</Td>
                {/* 3 clicks on 11,790 impressions is 0.03%, not "0.0%". */}
                <Td align="right">{fmtPercent(r.ctr * 100, r.ctr * 100 < 0.1 ? 2 : 1)}</Td>
                <Td align="right">{r.position.toFixed(1)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <Pagination {...pager} onPage={pager.setPage} />
        </>
      )}
    </Card>
  );
}

function QueryMovement({ data }: { data?: GtmMarketingData }) {
  const m = data?.queryMovement;
  return (
    <div className="space-y-6">
      <MovementTable title="Biggest gainers" subtitle="Queries that improved position (GSC)" rows={m?.improved} />
      <MovementTable title="Biggest drops" subtitle="Queries that lost position (GSC)" rows={m?.declined} />
    </div>
  );
}

function MovementTable({
  title, subtitle, rows,
}: { title: string; subtitle: string; rows?: GtmQueryMovementRow[] }) {
  // GSC rarely has clicks on these long-tail queries: a column of zeros and dots is noise.
  const list = rows ?? NO_ROWS;
  const showCtr = !mostlyEmpty(list, (r) => Number((r.ctr * 100).toFixed(1)));
  const showCtrDelta = !mostlyEmpty(list, (r) => Number((r.ctrDelta * 100).toFixed(1)));
  const showClicks = !mostlyEmpty(list, (r) => r.clicks);
  const hidden = [!showCtr && 'CTR', !showCtrDelta && 'CTR change', !showClicks && 'clicks'].filter(Boolean);
  const widths = ['auto', '6rem', '6rem', ...(showCtr ? ['6rem'] : []), ...(showCtrDelta ? ['6rem'] : []), ...(showClicks ? ['6rem'] : [])];
  return (
    <Card>
      <CardHeader title={title} description={subtitle} />
      {!rows || rows.length === 0 ? (
        <EmptyState compact title="No movement data." />
      ) : (
        <>
        <Table minWidth={hidden.length > 0 ? 360 : 560} colWidths={widths}>
          <THead>
            <tr>
              <Th>Query</Th>
              <Th align="right">Position</Th>
              <Th align="right">Δ</Th>
              {showCtr && <Th align="right">CTR</Th>}
              {showCtrDelta && <Th align="right">CTR Δ</Th>}
              {showClicks && <Th align="right">Clicks</Th>}
            </tr>
          </THead>
          <tbody>
            {rows.map((r) => {
              const pos = Number(r.positionDelta.toFixed(1));
              const pp = Number((r.ctrDelta * 100).toFixed(1));
              return (
                <Tr key={r.query}>
                  <Td className="text-text-primary break-words" title={r.query}>{r.query}</Td>
                  <Td align="right">{r.position.toFixed(1)}</Td>
                  <Td align="right">
                    {/* Every row of a gainers (or drops) table moves the same way: plain numbers, the title says which. */}
                    {pos === 0 ? <Nil /> : <span className="tabular-nums">{fmtSigned(pos, 1)}</span>}
                  </Td>
                  {showCtr && <Td align="right">{fmtPercent(r.ctr * 100)}</Td>}
                  {showCtrDelta && (
                    <Td align="right">
                      {pp === 0 ? (
                        <Nil />
                      ) : (
                        <TrendPill trend={pp > 0 ? 'up' : 'down'}>
                          {fmtSigned(pp, 1, 'pp')}
                        </TrendPill>
                      )}
                    </Td>
                  )}
                  {showClicks && <Td align="right">{fmtNumber(r.clicks)}</Td>}
                </Tr>
              );
            })}
          </tbody>
        </Table>
        {hidden.length > 0 && (
          <TableFooter>
            {hidden.join(', ').replace(/^./, (c) => c.toUpperCase())} hidden: zero or empty on almost every query.
          </TableFooter>
        )}
        </>
      )}
    </Card>
  );
}

/**
 * The lead funnel, from one lead definition (see GtmLeadFunnel): each later
 * stage follows the period's leads to the deals their company opened after.
 * SAL is left out because the data can't tell it from "lead to deal opened".
 */
export function FunnelWaterfall({
  funnel,
  loading,
  pipelineDeals = null,
  demosBooked = null,
  demoSources = null,
}: {
  funnel: GtmLeadFunnel | null;
  loading: boolean;
  /** Every deal opened in the period (the "Pipeline created" card), for the bridge line. */
  pipelineDeals?: number | null;
  /** Demos booked in the period: the Overview's "Demos booked" tile (Calendly, Sales' demo count). */
  demosBooked?: number | null;
  /** Counted demos by booking source (same window and count as `demosBooked`). */
  demoSources?: GtmDemoBookingSources | null;
}) {
  // Webinar and event sign-ups are registrations, not demand for the product, and LinkedIn lead gen
  // forms open inside LinkedIn and never visit the site. Neither sits in the sessions chain.
  const registrations = funnel ? eventRegistrations(funnel) : 0;
  const websiteLeads = funnel ? Math.max(funnel.websiteFormLeads - registrations, 0) : 0;
  // Two groups, each rate is the row's own count over the row above it in the same group.
  type Row = { key: string; label: string; count: number; hint?: string; basis: string | null; rate: number | null; side?: boolean };
  const traffic: Row[] = [];
  if (funnel && funnel.sessions !== null) {
    const sessions = funnel.sessions;
    traffic.push({ key: 'sessions', label: 'Sessions (GA4)', count: sessions, basis: null, rate: null });
    traffic.push({
      key: 'website',
      label: 'Website leads',
      count: websiteLeads,
      hint: 'People who sent a website form other than a webinar sign-up (free trial, demo request, pricing). LinkedIn lead forms are not included.',
      basis: 'of sessions',
      rate: sessions > 0 ? (websiteLeads / sessions) * 100 : null,
    });
  }
  const deals: Row[] = funnel
    ? [
        { key: 'leads', label: 'Leads', count: funnel.leads, hint: 'Every lead form in the period: website, webinar and LinkedIn', basis: null, rate: null },
        { key: 'mql', label: 'Lead → deal opened', count: funnel.mql, hint: 'The lead’s company opened a deal (any type) on or after the day they submitted. Not the MQL target’s MQL: see “MQLs vs target”.', basis: 'of leads', rate: funnel.leads ? (funnel.mql / funnel.leads) * 100 : null },
        { key: 'sql', label: 'SQL', count: funnel.sql, hint: 'That deal reached proposal or later', basis: 'of deals opened', rate: funnel.mql ? (funnel.sql / funnel.mql) * 100 : null },
        { key: 'customer', label: 'Customer', count: funnel.customer, hint: 'That deal was won', basis: 'of SQL', rate: funnel.sql ? (funnel.customer / funnel.sql) * 100 : null },
      ]
    : [];
  const max = Math.max(...[...traffic, ...deals].map((r) => r.count), 1);
  const renderRow = (r: Row) => (
    <div key={r.key} className="grid grid-cols-[8rem_1fr_3.5rem_7rem] items-center gap-3">
      <span className="truncate text-body-sm text-text-primary" title={r.hint}>
        {r.label}
      </span>
      {/* Width is the row's share of the biggest row, so every bar reads at its true size. */}
      <span className="relative h-2.5 rounded-full bg-surface-tertiary">
        <span
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${Math.max((r.count / max) * 100, r.count > 0 ? 0.75 : 0)}%`, background: CHART_SERIES[0] }}
        />
      </span>
      <span className="text-right text-body-sm font-medium tabular-nums text-text-primary">{fmtNumber(r.count)}</span>
      <span className="text-right text-body-xs tabular-nums text-text-tertiary" title={r.hint}>
        {r.rate === null ? '' : `${fmtRate(r.rate)} ${r.basis}`}
      </span>
    </div>
  );
  return (
    <Card>
      <CardHeader
        title="Funnel"
        description="Every rate is the row’s count divided by the count named next to it (GA4 + forms + Calendly + Attio)"
        action={
          <InfoTip label="How the funnel is counted">
            Two chains. Traffic: sessions, then website leads (free trial, demo request and pricing forms) as a share of
            sessions. Deals: every lead form in the period (website, webinar, LinkedIn lead gen), then “lead to deal opened”
            (called MQL until 15 Oct; the MQL target has its own card), SQL and customer, which follow those leads to the
            deals their company opened afterwards. Demos booked sit below, as a count by booking source, not as a share of
            sessions. Test addresses and the SDRs’ personal demo forms are left out.
          </InfoTip>
        }
      />
      {loading ? (
        <LoadingState />
      ) : !funnel ? (
        <EmptyState compact title="Lead data unavailable for this period." />
      ) : (
        <>
          {traffic.length > 0 && (
            <div className="space-y-3">
              <p className="eyebrow">Website traffic</p>
              {traffic.map(renderRow)}
            </div>
          )}
          {demosBooked !== null && (
            <div className={`space-y-2 ${traffic.length > 0 ? 'mt-6' : ''}`}>
              <p className="eyebrow">Demos booked · all motions</p>
              <p className="text-body-sm text-text-primary">
                <span className="font-medium tabular-nums">{fmtNumber(demosBooked)}</span> demos booked in the period, whichever way they were booked
                (a demo canceled later still counts here; the MQL card leaves canceled demos out).
              </p>
              {demoSources && demoSources.bySource.length > 0 && (
                <ul className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
                  {demoSources.bySource.map((r) => (
                    <li key={r.source} className="flex items-baseline justify-between gap-3 text-body-sm">
                      <span className="text-text-secondary">{r.label}</span>
                      <span className="font-medium tabular-nums text-text-primary">{fmtNumber(r.count)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-body-xs text-text-secondary">
                Most bookings are made directly in Calendly, so the source per motion is only partly known until Calendly links
                carry a motion (planned with sales). A link tagged with utm_source or utm_campaign shows up here as “Marketing link”.
              </p>
            </div>
          )}
          <div className={`space-y-3 ${traffic.length > 0 ? 'mt-6' : ''}`}>
            <p className="eyebrow">Lead forms to deals</p>
            {deals.map(renderRow)}
            <p className="text-body-xs text-text-secondary">
              {fmtNumber(funnel.leads)} leads = {fmtNumber(websiteLeads)} website leads + {fmtNumber(registrations)} webinar {registrations === 1 ? 'registration' : 'registrations'} +{' '}
              {fmtNumber(funnel.linkedinFormLeads)} LinkedIn lead forms
              {(funnel.bothSources ?? 0) > 0 && `, minus ${fmtNumber(funnel.bothSources!)} ${funnel.bothSources === 1 ? 'person' : 'people'} who sent both (counted once)`}
              .
            </p>
          </div>
          <TableFooter>
            <span className="inline-flex flex-wrap items-center gap-1.5">
              {funnel.byForm.length > 0 && (
                <span>
                  Leads by form: {funnel.byForm.map((f) => `${f.form} ${fmtNumber(f.leads)}`).join(', ')}
                  {(funnel.bothSources ?? 0) > 0 && `, minus ${fmtNumber(funnel.bothSources!)} counted in both website and LinkedIn`}
                  {' = '}
                  {fmtNumber(funnel.leads)}.
                </span>
              )}
              <InfoTip label="Funnel notes">
                {(funnel.multiFormLeads ?? 0) > 0 &&
                  `${fmtNumber(funnel.multiFormLeads!)} ${funnel.multiFormLeads === 1 ? 'person' : 'people'} sent more than one website form and ${funnel.multiFormLeads === 1 ? 'is' : 'are'} counted under the first. `}
                {funnel.sessions !== null &&
                  'Sessions are GA4’s (all traffic, consent-dependent); Website shows the OS’s own tracking, which counts differently. '}
                {funnel.excluded.tests + funnel.excluded.otherFormSubmissions > 0 &&
                  `Left out: ${fmtNumber(funnel.excluded.tests)} test (test address or a “test” UTM) and ${fmtNumber(funnel.excluded.otherFormSubmissions)} non-lead-form submissions. `}
                SAL is hidden: every Attio deal stage in use maps to SAL or later, so it would only repeat “lead to deal opened”.
                {pipelineDeals !== null &&
                  ` Deal opened and SQL follow only this period’s form leads to their companies’ deals; “Pipeline created” counts all ${fmtNumber(pipelineDeals)} ${pipelineDeals === 1 ? 'deal' : 'deals'} opened in the period, from every channel, outbound and no channel recorded included.`}
                {funnel.sessions === null && ' Sessions and website leads are hidden: no GA4 data for this period.'}
              </InfoTip>
            </span>
          </TableFooter>
        </>
      )}
    </Card>
  );
}

/** Website leads whose first form was a webinar or event sign-up (each person once, as in `byForm`). */
function eventRegistrations(funnel: GtmLeadFunnel): number {
  return funnel.byForm.filter((f) => f.source === 'website' && /webinar|event/i.test(f.form)).reduce((n, f) => n + f.leads, 0);
}

function MarketConversion({ rows, funnel }: { rows?: GtmMarketConversionRow[] | null; funnel: GtmLeadFunnel | null }) {
  // Website-form leads per country (the funnel's definition). An older API build sends all-source
  // leads only; then the column says so and the footer names the leads that have no country.
  const websiteOnly = !!rows && rows.length > 0 && rows.every((r) => r.websiteLeads !== undefined);
  const leadsOf = (r: GtmMarketConversionRow) => (websiteOnly ? (r.websiteLeads ?? 0) : r.leads);
  const shown = rows?.reduce((n, r) => n + leadsOf(r), 0) ?? 0;
  const websiteTotal = funnel ? Math.max(funnel.websiteFormLeads - eventRegistrations(funnel), 0) : 0;
  const otherMarkets = funnel ? Math.max(0, funnel.leads - funnel.noCountry - shown) : 0;
  return (
    <Card>
      <CardHeader
        title="Market conversion"
        description={
          websiteOnly
            ? 'Sessions → website leads by country. Sessions are by visitor country (GA4); leads are by the company’s country in Attio, so the two can differ'
            : 'Sessions → leads by country. Sessions are by visitor country (GA4); leads are by the company’s country in Attio, so the two can differ'
        }
        action={
          <InfoTip label="About these sessions">
            GA4 sessions include SEQUESTO’s own visits: the property has no internal-traffic filter that we can see, and Belgium
            is inflated by them. Sessions count where the visitor is; leads count where their company is, so a Belgian visit
            by a Dutch company’s employee is a Belgium session and a Netherlands lead. Read the rates as rough.
          </InfoTip>
        }
      />
      {!rows || rows.length === 0 ? (
        <EmptyState compact title="No market data for this period." />
      ) : (
        <>
          <Table>
            <THead>
              <tr>
                <Th>Market</Th>
                <Th align="right">Sessions</Th>
                <Th align="right">{websiteOnly ? 'Website leads' : 'Leads (all sources)'}</Th>
                <Th align="right">{websiteOnly ? 'Conv. rate' : 'Leads per session'}</Th>
              </tr>
            </THead>
            <tbody>
              {rows.map((r) => {
                const n = leadsOf(r);
                return (
                  <Tr key={r.country}>
                    <Td className="text-text-primary">{countryName(r.country)}</Td>
                    <Td align="right">{fmtNumber(r.sessions)}</Td>
                    <Td align="right">{n === 0 ? <Nil /> : fmtNumber(n)}</Td>
                    <Td align="right">{r.sessions > 0 && n > 0 ? fmtRate((n / r.sessions) * 100) : <Nil />}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          <p className="mt-3 text-body-xs text-text-secondary">
            GA4 sessions include SEQUESTO’s own visits (no internal-traffic filter is active that we can see), so Belgium is inflated.
            Sessions are by visitor country, leads by company country.
          </p>
          {funnel && (
            <TableFooter>
              {websiteOnly
                ? `${fmtNumber(shown)} of ${fmtNumber(websiteTotal)} website leads are in these markets; the rest are elsewhere or have no known country.`
                : `${fmtNumber(funnel.leads)} leads = ${fmtNumber(shown)} in these markets + ${fmtNumber(otherMarkets)} in other markets + ${fmtNumber(funnel.noCountry)} with no known country. ${fmtNumber(funnel.noCountry)} ${funnel.noCountry === 1 ? 'lead has' : 'leads have'} no country; LinkedIn and webinar leads never visit the site, so these rates read high.`}
            </TableFooter>
          )}
        </>
      )}
    </Card>
  );
}

function stripDomain(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}
